import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Paper, Typography, Card, CardContent, Link } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { useQuery, useQueryHistory, useDocumentSummary } from '../../../hooks/useDocs';
import { AI_MODELS, DEFAULT_TOP_K, MIN_TOP_K, MAX_TOP_K } from '../constants';
import { APP_PAGES } from '../../../pages';
import QueryInput from './QueryInput';
import ModelSelector from './ModelSelector';
import ChunkSlider from './ChunkSlider';
import AnswerCard from './AnswerCard';
import ChunkViewer from './ChunkViewer';
import SourcesList from './SourcesList';
import QueryHistory from './QueryHistory';

const Query: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const hasAutoSubmitted = useRef(false);

  const [query, setQuery] = useState(initialQuery);
  const [model, setModel] = useState<'gpt-4o' | 'gpt-4' | 'gpt-4o-mini'>('gpt-4');
  const [topK, setTopK] = useState(DEFAULT_TOP_K);

  const { submitQuery, result: queryResult, isLoading: isQueryLoading, error: queryError } = useQuery();
  const { addToHistory } = useQueryHistory();
  const { summary: stats, isLoading: statsLoading } = useDocumentSummary();

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  const handleSubmit = async () => {
    if (!query.trim()) return;

    try {
      const response = await submitQuery({ query: query.trim(), topK, model });
      addToHistory({
        query: query.trim(),
        model,
        answerPreview: response.answer.substring(0, 100),
      });
    } catch (error) {
      // Error handled by hook
    }
  };

  const isLoading = isQueryLoading;
  const error = queryError;

  const StatsCard: React.FC<{ title: string; value: number }> = ({ title, value }) => (
    <Card
      sx={{
        height: '100%',
        border: '1px solid #D9D9D9',
        borderRadius: '0.188rem',
        boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.15)',
      }}
    >
      <CardContent>
        <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
          {statsLoading ? '—' : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(APP_PAGES.DOCS_LIBRARY)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            data-testid="docs-library-link"
          >
            <FolderIcon fontSize="small" />
            Document Library
          </Link>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Total Documents" value={stats.total} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Ready" value={stats.ready} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Processing" value={stats.processing} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatsCard title="Failed" value={stats.failed} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <QueryInput
                value={query}
                onChange={setQuery}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                data-testid="docs-query-input"
              />

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <ModelSelector
                    value={model}
                    onChange={setModel}
                    models={AI_MODELS}
                    data-testid="docs-model-selector"
                  />
                  <ChunkSlider
                    value={topK}
                    onChange={setTopK}
                    min={MIN_TOP_K}
                    max={MAX_TOP_K}
                    data-testid="docs-chunk-slider"
                  />
                </Box>

              </Box>
            </Paper>

            {/* Results */}
            {error && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: '#FFEBEE' }}>
                <Typography color="error">{error}</Typography>
              </Paper>
            )}

            {queryResult && (
              <>
                <AnswerCard answer={queryResult.answer} data-testid="docs-answer-card" />
                {queryResult.chunks && queryResult.chunks.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <ChunkViewer chunks={queryResult.chunks} data-testid="docs-chunk-viewer" />
                  </Box>
                )}
                <Box sx={{ mt: 3 }}>
                  <SourcesList sources={queryResult.sources} chunks={queryResult.chunks || []} data-testid="docs-sources-list" />
                </Box>
              </>
            )}

            {!isLoading && !error && !queryResult && query && (
              <Paper sx={{ p: 3 }}>
                <Typography color="text.secondary" textAlign="center">
                  Enter a query above to search your documents
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Sidebar - Query History */}
          <Grid item xs={12} md={3}>
            <QueryHistory onSelectQuery={(q) => setQuery(q)} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Query;
